import React, { useState } from 'react';
import { updateAuthUserAction } from '../../redux/actions/firebase-actions';
import { setLocation } from '../../redux/actions/app-actions';
import { UILocation } from '../../redux/reducers/app-reducers';
import { GoogleButton } from '../google-button';
import {
  Box,
  TextField,
  Button,
  SxProps,
  FormLabel,
  Link,
} from '@mui/material';
import { Centered } from 'its-pages';
import { StyledForm } from '../form-utils';
import { ITSImage } from '../its-image';
import { useClientDispatch, useClientSelector } from '../../redux/hooks';


const INITIAL_STATE = {
  email: '',
  password: '',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authError: null as any,
};

export const SignInPage= () => {
  const dispatch = useClientDispatch();
  const firebase = useClientSelector((state) => state.firebase.firebase);
  const [formState, updateForm] = useState(INITIAL_STATE);
  const { email, password, authError } = formState;

  function validateForm() {
    return email.length > 0 && password.length > 0;
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const { email, password } = formState;

    if (firebase) {
      firebase
        .doSignInWithEmailAndPassword(email, password)
        .then((auth) => {
          const firebaseUser = auth.user;
          const authUser = { firebaseUser };
          dispatch(updateAuthUserAction(authUser));
          updateForm({ ...INITIAL_STATE });
          dispatch(setLocation(UILocation.home));
        })
        .catch((error) => {
          updateForm({ ...formState, authError: error });
        });
    }

    event.preventDefault();
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateForm({ ...formState, [event.target.name]: event.target.value });
  };

  const onClick = (location: UILocation) => {
    dispatch(setLocation(location));
  };

  const gridContainer: SxProps = {
    display: 'grid',
    width: '100%',
    maxWidth: '640px',
    gridTemplateAreas: `
      "image image" 
      "email password" 
      ${authError ? `"error error"` : ''}
      "login login" 
      "or or"
      "google google"
      "sign-up forgot-password"`,
    gridTemplateRows: `auto 1fr ${authError ? '0.25fr' : ''} 1fr 1fr 1fr 1fr`,
    gridTemplateColumns: 'content content',
    gap: 1,
  };
  return (
    <Centered>
      <StyledForm onSubmit={onSubmit}>
        <Box sx={gridContainer}>
          <ITSImage gridArea='image' />
          <TextField
            sx={{ gridArea: 'email' }}
            name='email'
            type='text'
            label='email'
            fullWidth
            variant='outlined'
            onChange={onChange}
          />
          <TextField
            sx={{ gridArea: 'password' }}
            name='password'
            type='password'
            label='password'
            fullWidth
            variant='outlined'
            onChange={onChange}
          />
          {authError ? (
            <FormLabel
              sx={{
                gridArea: 'error',
                fontSize: 'small',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              error
            >
              {authError.message}
            </FormLabel>
          ) : null}
          <Button
            type='submit'
            disabled={!validateForm()}
            fullWidth
            size='large'
            sx={{
              gridArea: 'login',
              backgroundColor: validateForm()
                ? 'var(--Action-Background)'
                : 'var(--Action-Background-Disabled)',
              color: 'var(--Action-Foreground)',
            }}
          >
            Login
          </Button>
          <FormLabel
            sx={{
              gridArea: 'or',
              fontSize: 'xx-large',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            or
          </FormLabel>
          <GoogleButton
            size='large'
            onClick={() => firebase?.doSignInWithPopup('google')}
            buttonText='Sign In with Google'
            className={'mt-1'}
            sx={{
              gridArea: 'google',
              backgroundColor: 'var(--Action-Background)',
              color: 'var(--Action-Foreground)',
            }}
          />
          <Link
            component={'button'}
            sx={{ gridArea: 'sign-up' }}
            onClick={() => onClick(UILocation.signup)}
          >
            Don&apos;t have an account? Sign up here.
          </Link>
          <Link
            component={'button'}
            sx={{ gridArea: 'forgot-password' }}
            onClick={() => onClick(UILocation.forgotPassword)}
            display='flex'
            justifyContent='flex-end'
            alignItems={'center'}
          >
            Forgot password? Click here.{' '}
          </Link>
        </Box>
      </StyledForm>
    </Centered>
  );
};
